import { FC, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Selector from '../../components/shared/Selector/Selector';
import Tabs from '../../components/shared/Tabs';
import { ServiceSupplier } from '../../services/service-supplier';
import { serviceAreas } from '../../services/service-areas';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';
import appState from '../../state/AppStateContainer';
import './BulkManageCategoriesModal.scss';

type AreaOption = { value: string; label: string; __isNew__?: boolean };

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  areas: AreaOption[];
  supplierIds: string[];
  onSave: () => void;
  fetchAreas: () => Promise<void>;
};

const BulkManageCategoriesModal: FC<Props> = ({ show, setShow, areas, supplierIds, onSave, fetchAreas }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'bulkManageCategories' });
  // Creating a brand-new org-wide category (/areas/add) is admin-only; write-access
  // users (Inköp/Deltagare) can assign and remove existing categories.
  const canCreateAreas = appState.isAdminRole();
  const [selectedAreas, setSelectedAreas] = useState<AreaOption[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    setSelectedAreas([]);
  };

  // The creatable Selector surfaces a typed-but-unsaved entry as { __isNew__: true }.
  // Persist it as a real area, then swap the placeholder for the saved {value,label}.
  const handleChange = async (selected: AreaOption[]) => {
    const options = selected || [];
    const newOption = options.find(o => o.__isNew__);
    if (!newOption) {
      setSelectedAreas(options);
      return;
    }
    try {
      const updatedAreas = await serviceAreas.addArea({ name: newOption.label });
      await fetchAreas();
      const created = updatedAreas.find((a: any) => a.name === newOption.label);
      const withoutNew = options.filter(o => !o.__isNew__);
      setSelectedAreas(created ? [...withoutNew, { value: created._id, label: created.name }] : withoutNew);
    } catch {
      setSelectedAreas(options.filter(o => !o.__isNew__));
      NotificationManager.error(
        t('notification.createError.description', 'Kunde inte skapa kategorin'),
        t('notification.createError.title', 'Något gick fel')
      );
    }
  };

  const handleSave = async () => {
    if (!selectedAreas.length) return;
    const areaIds = selectedAreas.map(a => a.value);
    setLoading(true);
    try {
      const serviceCall = tab === 0 ? ServiceSupplier.bulkAddAreas : ServiceSupplier.bulkRemoveAreas;
      await serviceCall(supplierIds, areaIds);
      NotificationManager.success(
        t('notification.success.description', 'Kategorierna har uppdaterats'),
        t('notification.success.title', 'Sparat')
      );
      onSave();
      setShow(false);
      setSelectedAreas([]);
    } catch (e) {
      const errorMsg = (e as any)?.response?.data?.error ?? t('notification.error.description', 'Kunde inte uppdatera kategorierna');
      NotificationManager.error(errorMsg, t('notification.error.title', 'Något gick fel'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      setShow={(v: boolean) => { if (!v) setSelectedAreas([]); setShow(v); }}
      title={t('title', 'Hantera kategorier')}
      buttonInfo={{
        label: t('button.save', 'Spara'),
        action: handleSave,
        loaderShow: loading,
        disabled: selectedAreas.length === 0,
      }}
    >
      <div style={{ fontSize: '14px', color: 'var(--gray-700)', width: '500px', display: 'flex', gap: '16px', flexDirection: 'column' }}>
        <Tabs
          tabs={[t('tabs.add', 'Lägg till'), t('tabs.remove', 'Ta bort')]}
          selected={tab}
          onChange={handleTabChange}
        />
        <Selector
          className="bulk-categories-selector"
          placeholder={t('placeholder.categories', 'Välj kategorier')}
          options={areas}
          value={selectedAreas}
          onChange={(selected: any) => handleChange(selected)}
          isMulti
          isCreatable={tab === 0 && canCreateAreas}
        />
      </div>
    </Modal>
  );
};

export default BulkManageCategoriesModal;
